import { createContext, useContext } from "react";
import type { LoginRequest, PublicUser, RegisterRequest } from "@nh/shared";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export interface AuthContextValue {
  user: PublicUser | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  login: (input: LoginRequest) => Promise<PublicUser>;
  /** Entrar (o registrarse) con el `credential` del botón de Google. */
  loginWithGoogle: (credential: string) => Promise<PublicUser>;
  register: (input: RegisterRequest) => Promise<PublicUser>;
  logout: () => Promise<void>;
  /** Restaura la sesión desde la cookie httpOnly (lo usa el callback de Google). */
  restore: () => Promise<PublicUser>;
}

/**
 * El contexto de sesión, SOLO.
 *
 * ══════════════════════════════════════════════════════════════════════
 * ESTE ARCHIVO NO PUEDE EXPORTAR NINGÚN COMPONENTE. Es su razón de ser.
 *
 * Vivía junto a `AuthProvider`, y eso provocaba el error que Anahí veía
 * aparecer y desaparecer al navegar: *"useAuth se usó fuera de
 * <AuthProvider>"*, con el `AppShell` en la traza.
 *
 * La causa es la recarga en caliente. Cuando un módulo exporta un
 * componente Y algo que no lo es, Fast Refresh no puede actualizarlo
 * parcialmente sin reevaluarlo entero — y al reevaluarlo, `createContext`
 * se ejecuta OTRA VEZ y nace un contexto nuevo. Durante ese instante el
 * `AuthProvider` que está montado sigue publicando el contexto viejo
 * mientras los consumidores ya leen el nuevo: `useContext` devuelve
 * `null` y el hook lanza. De ahí que apareciera "a ratos" y se arreglara
 * al recargar la página a mano.
 *
 * Con el contexto aquí, editar el proveedor —que es lo que se toca a
 * diario— ya no lo recrea. Y si se edita ESTE archivo, como no exporta
 * componentes, Fast Refresh no intenta el parche: recarga la página
 * entera, que es lo correcto y no da error.
 *
 * Se ve en la propia traza del fallo: `auth.context.tsx?t=1787784815270`.
 * Ese `?t=` es el módulo parcheado en caliente.
 * ══════════════════════════════════════════════════════════════════════
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * La sesión. Lanza si se usa fuera del proveedor, en vez de devolver
 * `null`: un componente que cree tener sesión y no la tiene falla más
 * tarde y más lejos, con un error que ya no señala la causa.
 */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth se usó fuera de <AuthProvider>.");
  return value;
}
